import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { Segment, SegmentCriteria } from '../../shared/types/segment.interface';
import { ApiService } from '../../services/api.service';
import { PackageService } from '../../services/package.service';
import { Package } from '../../shared/types';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';

/**
 * Displays detailed information about a Segment in a modal dialog, formats and maps segment criteria, and exposes controls to edit or delete the segment.
 * Key methods:
 *   - loadSegmentDetails(): Loads full segment details from the API when a segment is provided and modal is visible.
 *   - getCriteriaDisplayText(criteria): Formats a single criteria into a readable string, mapping condition tokens to symbols/words.
 *   - shouldDisplayAsList(criteria): Determines when criteria should be rendered as a list (e.g., vehicle makes, packages, or date ranges).
 *   - getCriteriaTypesList(criteria): Returns an array of displayable values for criteria types (maps package IDs to names, or returns date range values).
 *   - getCriteriaLabelAndCondition(criteria): Returns the label and condition for list displays.
 *   - getStatusDisplay / getStatusClass: Helpers to display status text and CSS class.
 *   - onEditSegment() / onDeleteSegment() / onClose(): Emit corresponding output events and manage dropdown/visibility state.
 * Lifecycle hooks:
 *   - ngOnInit(): Loads package data used to map IDs to names.
 *   - ngOnChanges(changes): Triggers loadSegmentDetails() when the input segment changes and the modal is visible.
 * @component
 * @selector app-segment-details-modal
 * @example
 *   <app-segment-details-modal
 *     [visible]="true"
 *     [segment]="selectedSegment"
 *     (close)="handleClose()"
 *     (editSegment)="handleEdit($event)"
 *     (deleteSegment)="handleDelete($event)">
 *   </app-segment-details-modal>
 * @input {boolean} visible - Controls modal visibility; when true and a segment is provided the component fetches and displays details.
 * @input {Segment | null} segment - The segment summary or identifier; used to fetch and display full segment details.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed (e.g., user closes modal or after edit).
 * @output {EventEmitter<Segment>} editSegment - Emitted when the user chooses to edit the currently displayed segment; emits the segment details.
 * @output {EventEmitter<Segment>} deleteSegment - Emitted when the user confirms deletion of the segment; emits the segment reference.
 */
@Component({
  selector: 'app-segment-details-modal',
  standalone: true,
  imports: [CommonModule, MatButtonModule, ClickOutsideDirective],
  templateUrl: './segment-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./segment-details-modal.component.scss'],
})
export class SegmentDetailsModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() segment: Segment | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() editSegment = new EventEmitter<Segment>();
  @Output() deleteSegment = new EventEmitter<Segment>();

  segmentDetails: Segment | null = null;
  isLoading = false;
  showDropdown = false;

  private readonly CRITERIA_CONDITION_MAP: Record<string, string> = {
    equals: '=',
    does_not_equal: '≠',
    less_than: '<',
    less_than_or_equal_to: '≤',
    greater_than: '>',
    greater_than_or_equal: '≥',
    is: 'IS',
    is_not: 'IS NOT',
    between: 'BETWEEN',
    on: 'ON',
    before: 'BEFORE',
    after: 'AFTER',
    in_the_last: 'IN THE LAST',
    not_in_the_last: 'NOT IN THE LAST',
  };
  packages: Package[] = [];
  packageMap: Record<string, string> = {};

  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private packageService: PackageService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.loadPackages();
      if (this.segment) {
        this.loadSegmentDetails();
      }
    } else if (changes['segment'] && this.segment && this.visible) {
      this.loadSegmentDetails();
    }
  }

  /**
   * Loads the details for the current segment (if present) by calling the API, updates component state (segmentDetails and isLoading), and logs errors.
   * @example
   * oadSegmentDetails()
   * undefined
   * @returns {{void}} Performs an HTTP request and updates component state; does not return a value.
   **/
  loadSegmentDetails(): void {
    if (!this.segment?.id) return;

    this.isLoading = true;
    this.apiService.getSegmentById(this.segment.id).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.segmentDetails = response.data;
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading segment details:', error);
        this.isLoading = false;
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  getStatusDisplay(status: boolean): string {
    return status ? 'Active' : 'Inactive';
  }

  getStatusClass(status: boolean): string {
    return status ? 'status-active' : 'status-inactive';
  }

  /**
   * Build a human-readable text representation of a segment criteria object.
   * @example
   * etCriteriaDisplayText({ criteria: 'signup_date', criteria_condition: 'between', start_date: '2021-01-01', end_date: '2021-12-31' })
   * "SIGNUP DATE BETWEEN 2021-01-01 AND 2021-12-31"
   * @param {SegmentCriteria} criteria - The criteria object describing a segment filter.
   * @returns {string} A formatted string describing the provided criteria.
   **/
  getCriteriaDisplayText(criteria: SegmentCriteria): string {
    if (!criteria) return '';
    const condition =
      this.CRITERIA_CONDITION_MAP[criteria.criteria_condition] ||
      criteria.criteria_condition?.toUpperCase() ||
      '';
    const criteriaName = criteria.criteria?.toUpperCase() || '';

    let result = '';
    if (criteria.criteria_value !== null && criteria.criteria_value !== undefined) {
      result = `${criteriaName} ${condition} ${criteria.criteria_value}`;
    } else if (criteria.start_date && criteria.end_date) {
      result = `${criteriaName} ${condition} ${criteria.start_date} AND ${criteria.end_date}`;
    } else if (criteria.start_date) {
      result = `${criteriaName} ${condition} ${criteria.start_date}`;
    } else if (criteria.days !== null && criteria.days !== undefined) {
      result = `${criteriaName} ${condition} ${criteria.days} DAYS`;
    } else if (criteria.criteria_types && criteria.criteria_types.length > 0) {
      result = `${criteriaName} ${condition} ${criteria.criteria_types.join(', ')}`;
    } else {
      result = `${criteriaName} ${condition}`;
    }

    // Replace underscores with spaces
    return result.replace(/_/g, ' ');
  }

  // New method to check if criteria should display as a list
  /**
   * Decide if segment criteria should be displayed as a list (true for vehicle/package types or date ranges).
   * @example
   * houldDisplayAsList({ criteria: 'vehicle makes', criteria_types: ['Make'], start_date: null, end_date: null })
   * true
   * @param {{SegmentCriteria}} {{criteria}} - Segment criteria object to evaluate whether it should render as a list.
   * @returns {{boolean}} Whether the criteria should be displayed as a list.
   **/
  shouldDisplayAsList(criteria: SegmentCriteria): boolean {
    const hasTypesList = Boolean(
      criteria.criteria_types &&
      criteria.criteria_types.length > 0 &&
      (criteria.criteria?.toLowerCase().includes('vehicle') ||
        criteria.criteria?.toLowerCase().includes('package'))
    );
    const hasDateRange = Boolean(
      criteria.start_date && criteria.end_date && criteria.criteria?.toLowerCase().includes('date')
    );
    return hasTypesList || hasDateRange;
  }

  // New method to get criteria types for list display
  /**
   * Return a list of criteria type strings for a segment; maps package IDs to names or returns date range values when applicable.
   * @example
   * etCriteriaTypesList({ criteria: 'package', criteria_types: ['pkg_123','pkg_456'] })
   * ['Awesome Package', 'Pro Package']
   * @param {{SegmentCriteria}} {{criteria}} - Segment criteria object that may include criteria, criteria_types, start_date and end_date.
   * @returns {{string[]}} Return an array of strings representing criteria types, resolved package names, or a two-element date range.
   **/
  getCriteriaTypesList(criteria: SegmentCriteria): string[] {
    const criteriaTypes = criteria.criteria_types || [];

    // If this is a package criteria, map IDs to package names
    if (criteria.criteria?.toLowerCase().includes('package')) {
      return criteriaTypes.map(id => this.getPackageNameById(id));
    }

    // If this is a date criteria with date range, return the date values
    if (
      criteria.criteria?.toLowerCase().includes('date') &&
      criteria.start_date &&
      criteria.end_date
    ) {
      return [criteria.start_date, criteria.end_date];
    }

    return criteriaTypes;
  }

  // Load packages and create ID to name mapping
  private loadPackages(): void {
    this.packageService.loadPackages();
    this.packageService.packages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((packages: Package[]) => {
        this.packages = packages;
        // Create mapping from package ID to package name
        this.packageMap = {};
        packages.forEach(pkg => {
          this.packageMap[pkg.id] = pkg.package_name;
        });
      });
  }

  // Get package name by ID
  private getPackageNameById(packageId: string): string {
    return this.packageMap[packageId] || packageId; // Fallback to ID if name not found
  }

  // New method to get criteria label and condition for list display
  getCriteriaLabelAndCondition(criteria: SegmentCriteria): string {
    if (!criteria) return '';
    const condition =
      this.CRITERIA_CONDITION_MAP[criteria.criteria_condition] ||
      criteria.criteria_condition?.toUpperCase() ||
      '';
    const criteriaName = criteria.criteria?.toUpperCase() || '';

    const result = `${criteriaName} ${condition}`;
    // Replace underscores with spaces
    return result.replace(/_/g, ' ');
  }

  getJoinRuleDisplay(joinRule: string | null): string {
    return joinRule ? joinRule.toUpperCase() : 'AND';
  }

  hasCriteria(): boolean {
    return Boolean(
      this.segmentDetails?.criteria_list && this.segmentDetails.criteria_list.length > 0
    );
  }

  getCriteriaList(): SegmentCriteria[] {
    return this.segmentDetails?.criteria_list || [];
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  onEditSegment(): void {
    if (this.segmentDetails) {
      this.editSegment.emit(this.segmentDetails);
      this.showDropdown = false;
      this.close.emit();
    }
  }

  onDeleteSegment(): void {
    if (this.segment) {
      this.deleteSegment.emit(this.segment);
      this.showDropdown = false;
    }
  }

  onDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
